import React from 'react';
import { FilterState, LoanOfficer } from '../../types/throughput';
import { Button } from '../ui/Button';
import { RotateCcw, X } from 'lucide-react';
interface FilterBarProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  officers: LoanOfficer[];
  onRefresh: () => void;
}
export function FilterBar({
  filters,
  setFilters,
  officers,
  onRefresh
}: FilterBarProps) {
  const teamLeads = Array.from(new Set(officers.filter(o => o.role === 'Team Lead').map(o => JSON.stringify({
    id: o.id,
    name: o.name
  })))).map(s => JSON.parse(s));
  const loanOfficers = officers.filter(o => o.role === 'Loan Officer');
  const inputClass = 'h-9 px-3 text-sm bg-white border border-[#DDDBDA] rounded-md focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20 focus:border-[#7C3AED] transition-colors';
  const labelClass = 'block text-xs font-medium text-[#706E6B] mb-1';
  const handleScopeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === 'All') {
      setFilters({
        ...filters,
        scopeType: 'All',
        selectedScopeId: undefined
      });
    } else {
      const [type, id] = val.split('::');
      setFilters({
        ...filters,
        scopeType: type as any,
        selectedScopeId: id
      });
    }
  };
  const currentScopeValue = filters.scopeType === 'All' ? 'All' : `${filters.scopeType}::${filters.selectedScopeId}`;
  const hasActiveFilters = filters.leadSources.length > 0 || filters.scopeType !== 'All';
  const clearAllFilters = () => {
    setFilters({
      ...filters,
      scopeType: 'All',
      selectedScopeId: undefined,
      leadSources: []
    });
  };
  return <div className="sticky top-0 z-30 w-full bg-white border-b border-[#E5E5E5]" data-id="element-18">
      <div className="max-w-[1600px] mx-auto px-6 py-3" data-id="element-19">
        <div className="flex items-end justify-between" data-id="element-20">
          <div className="flex items-end gap-5" data-id="element-21">
            {/* Date Range */}
            <div className="flex items-end gap-2" data-id="element-22">
              <div data-id="element-23">
                <label className={labelClass} data-id="element-24">Start</label>
                <input type="date" value={filters.dateRange.start} onChange={e => setFilters({
                ...filters,
                dateRange: {
                  ...filters.dateRange,
                  start: e.target.value
                }
              })} className={`${inputClass} w-[138px]`} data-id="element-25" />
              </div>
              <span className="text-[#DDDBDA] pb-2 text-sm" data-id="element-26">–</span>
              <div data-id="element-27">
                <label className={labelClass} data-id="element-28">End</label>
                <input type="date" value={filters.dateRange.end} onChange={e => setFilters({
                ...filters,
                dateRange: {
                  ...filters.dateRange,
                  end: e.target.value
                }
              })} className={`${inputClass} w-[138px]`} data-id="element-29" />
              </div>
            </div>

            {/* Divider */}
            <div className="w-px h-9 bg-[#E5E5E5]" data-id="element-30" />

            {/* Scope */}
            <div data-id="element-31">
              <label className={labelClass} data-id="element-32">Scope</label>
              <select value={currentScopeValue} onChange={handleScopeChange} className={`${inputClass} w-[220px]`} data-id="element-33">
                <option value="All" data-id="element-34">All Loan Officers</option>
                <optgroup label="Team Leads" data-id="element-35">
                  {teamLeads.map((tl: any) => <option key={`Team Lead::${tl.id}`} value={`Team Lead::${tl.id}`} data-id="element-36">
                      {tl.name}
                    </option>)}
                </optgroup>
                <optgroup label="Loan Officers" data-id="element-37">
                  {loanOfficers.map(lo => <option key={`Loan Officer::${lo.id}`} value={`Loan Officer::${lo.id}`} data-id="element-38">
                      {lo.name}
                    </option>)}
                </optgroup>
              </select>
            </div>
          </div>

          <Button variant="neutral" size="sm" onClick={onRefresh} data-id="element-39">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" data-id="element-40" />
            Refresh
          </Button>
        </div>

        {/* Active filter chips */}
        {hasActiveFilters && <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-[#F0F0F0]" data-id="element-41">
            <span className="text-[11px] font-medium text-[#706E6B] uppercase tracking-wide mr-1" data-id="element-42">
              Active:
            </span>

            {filters.scopeType !== 'All' && filters.selectedScopeId && <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-xs font-medium bg-[#7C3AED]/8 text-[#7C3AED] rounded-md border border-[#7C3AED]/15" data-id="element-43">
                <span className="text-[#706E6B] font-normal" data-id="element-44">Officer:</span>
                {(() => {
            const officer = officers.find(o => o.id === filters.selectedScopeId);
            return officer ? officer.name : filters.selectedScopeId;
          })()}
                <button onClick={() => setFilters({
            ...filters,
            scopeType: 'All',
            selectedScopeId: undefined
          })} className="ml-0.5 hover:bg-[#7C3AED]/15 rounded p-0.5 transition-colors" data-id="element-45">
                  <X className="h-3 w-3" data-id="element-46" />
                </button>
              </span>}

            {filters.leadSources.map(source => <span key={source} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 text-xs font-medium bg-[#7C3AED]/8 text-[#7C3AED] rounded-md border border-[#7C3AED]/15" data-id="element-47">
                <span className="text-[#706E6B] font-normal" data-id="element-48">Source:</span>
                {source}
                <button onClick={() => setFilters({
            ...filters,
            leadSources: filters.leadSources.filter(s => s !== source)
          })} className="ml-0.5 hover:bg-[#7C3AED]/15 rounded p-0.5 transition-colors" data-id="element-49">
                  <X className="h-3 w-3" data-id="element-50" />
                </button>
              </span>)}

            <button onClick={clearAllFilters} className="text-[11px] font-medium text-[#706E6B] hover:text-[#181818] ml-1 transition-colors" data-id="element-51">
              Clear all
            </button>
          </div>}
      </div>
    </div>;
}